import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Header } from "../../../components/SpearParts/Layouts/Header";
import { RentalSearchSection } from "../../../components/Rental/Layout/RentalSearchSection";
import Axios from "../../../axios/axios";
import { Footer } from "../../../components/SpearParts/Layouts/Footer";
import NoData from "../../../components/NoData/Index";
import RentalAdvancedSearch from "./RentalAdvancedSearch";
import RntMiddle from "../../../components/Rental/Layout/RntMiddle";
import TopNavBar from "../../../components/SpearParts/Layouts/TopNavBar";
import TopSearchBar from "../../../components/SpearParts/Layouts/TopSearchBar";
import { toTitleCase } from "../../../components/utils/StringUtil";
import { resolveEntityName, getProductName, getProductPartNo, getProductYear } from "../../../components/utils/EntityResolver";

export default function RentalProductList() {
  const location = useLocation();
  const {
    rentals = [],
    rentalCategory = [],
    rentalBrand = [],
    rentalModels = [],
    region = [],
  } = location.state || {};
  const searchParams = new URLSearchParams(location.search);
  const selectedFiltersFromState = location.state?.selectedFilters || {};
  const initialFilters = {
    ...Object.fromEntries(searchParams.entries()),
    ...selectedFiltersFromState,
    subcategoryId:
      searchParams.get("subcategoryId") ||
      selectedFiltersFromState?.subcategoryId ||
      "",
  };

  const [filters, setFilters] = useState(initialFilters);

  const [filteredRentals, setFilteredRentals] = useState(rentals);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [originalRentals, setOriginalRentals] = useState(rentals);

  const [brandList, setBrandList] = useState(rentalBrand);


  const [categoryList, setCategoryList] = useState(rentalCategory);
  const [modelsList, setModelsList] = useState(rentalModels);
  const [regionList, setRegionList] = useState(region);

  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        if (!brandList.length) {
          const res = await Axios.get("/brand");
          if (res.data) setBrandList(Array.isArray(res.data) ? res.data : res.data.data || []);
        }
        if (!categoryList.length) {
          const res = await Axios.get("/categories");
          if (res.data) setCategoryList(Array.isArray(res.data) ? res.data : res.data.data || []);
        }
        if (!modelsList.length) {
          const res = await Axios.get("/model");
          if (res.data) setModelsList(Array.isArray(res.data) ? res.data : res.data.data || []);
        }
        if (!regionList.length) {
          const res = await Axios.get("/regions");
          if (res.data) setRegionList(Array.isArray(res.data) ? res.data : res.data.data || []);
        }
      } catch (err) {
        console.error("Error fetching master data in RentalProductList:", err);
      }
    };
    fetchMasterData();
  }, []);

  const navigate = useNavigate();

  const clearAllFilters = () => {
    setFilters({
      year: "",
      regionId: "",
      brandId: "",
      modelId: "",
      condition: "",
      availabilityStatus: "",
      features: "",
      certifications: "",
      subcategoryId: "",
      categoryId: "",
      productQuality: "",
    });
    navigate(location.pathname, { replace: true });
  };

  const removeSingleFilter = (keyToRemove) => {
    const updatedFilters = { ...filters };
    delete updatedFilters[keyToRemove];
    setFilters(updatedFilters);

    const searchParams = new URLSearchParams(location.search);
    searchParams.delete(keyToRemove);
    const newSearch = searchParams.toString();
    navigate(newSearch ? `${location.pathname}?${newSearch}` : location.pathname, { replace: true });
  };

  const getFilterLabel = (key, val) => {
    if (!val) return null;
    if (key === "categoryId") return `Category: ${getCategoryName(val, null)}`;
    if (key === "subcategoryId") return `Subcategory: ${val}`;
    if (key === "brandId") return `Brand: ${getBrandName(val)}`;
    if (key === "modelId") return `Model: ${getModelName(val)}`;
    if (key === "regionId") return `Region: ${getRegionName(val)}`;
    if (key === "year") return `Year: ${val}`;
    if (key === "condition") return `Condition: ${val}`;
    if (key === "availabilityStatus") return `Availability: ${val}`;
    if (key === "features") return `Feature: ${val}`;
    if (key === "certifications") return `Certification: ${val}`;
    if (key === "productQuality") return `Quality: ${toTitleCase(val)}`;
    return `${toTitleCase(key)}: ${val}`;
  };

  const urlSearchParamsObj = Object.fromEntries(new URLSearchParams(location.search).entries());
  const combinedActiveFilters = { ...urlSearchParamsObj, ...filters };
  delete combinedActiveFilters["page"];
  delete combinedActiveFilters["limit"];

  const activeFilterEntries = Object.entries(combinedActiveFilters)
    .filter(([k, v]) => v !== undefined && v !== null && v !== "")
    .map(([key, val]) => ({ key, label: getFilterLabel(key, val) }))
    .filter(item => item.label);

  const hasActiveFilters = activeFilterEntries.length > 0;

  const getBrandName = (brandId) => resolveEntityName(brandId, brandList, "Unknown Brand");
  const getModelName = (modelId) => resolveEntityName(modelId, modelsList, "Unknown Model");
  const getCategoryName = (categoryId, rental) => resolveEntityName(categoryId || rental?.subcategoryId || rental?.category, categoryList, "Unknown Category");
  const getRegionName = (regionId) => resolveEntityName(regionId, regionList, "Unknown Region");

  const [rentalsData, setRentalsData] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchRentals = async (updatedFilters = filters) => {

    setLoading(true);

    try {
      const query = new URLSearchParams(updatedFilters).toString();
      const response = await Axios.get(`/rental/search?${query}`);

      setRentalsData(response.data.data);
    } catch (err) {
      console.log(err);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchRentals();
  }, [filters]);
  useEffect(() => {
    if (location.state?.rentals) {
      setFilteredRentals(location.state.rentals);
      setOriginalRentals(location.state.rentals);
    }
  }, [location.state]);

  useEffect(() => {
    const fetchPage = async () => {
      try {
        const params = new URLSearchParams({
          ...filters,
          categoryId:
            filters.categoryId || searchParams.get("categoryId") || "",
          subcategoryId:
            filters.subcategoryId || searchParams.get("subcategoryId") || "",
          page,
          limit: itemsPerPage,
        });

        const res = await Axios.post(`/rental/search?${params.toString()}`);
        setFilteredRentals(res.data.data);
        setTotalPages(res.data.pages);
      } catch (error) {
        console.error(error);
      }
    };

    fetchPage();
  }, [filters, page]);

  // useEffect(() => {
  //   const params = new URLSearchParams(location.search);
  //   params.append("page", page);
  //   params.append("limit", 12);

  //   Axios.post(`/rental/search?${params.toString()}`)
  //     .then((res) => {
  //       setFilteredRentals(res.data.data);
  //       setTotalPages(res.data.pages);
  //     })
  //     .catch((err) => console.log(err));
  // }, [location.search, page]);

  const handlePageChange = async (newPage) => {
    setPage(newPage);

    const params = new URLSearchParams({
      ...filters,
      page: newPage,
      limit: itemsPerPage,
      categoryId: filters.categoryId || searchParams.get("categoryId") || "",
    });

    try {
      const res = await Axios.post(`/rental/search?${params.toString()}`);
      setFilteredRentals(res.data.data);
      setTotalPages(res.data.pages);
    } catch (err) {
      console.error(err);
    }
  };

  const sectionStyle = {
    backgroundSize: "cover",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
    backgroundImage: "url(/images/bnr-bg.png)",
  };

  const beforeImageStyle = {
    content: '""',
    position: "absolute",
    left: 0,
    top: "50px",
    width: "400px",
    height: "100%",
    backgroundImage: "url(/images/rental-before.png)",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "left top",
    backgroundSize: "contain",
    pointerEvents: "none",
    zIndex: 1,
  };

  const afterImageStyle = {
    content: '""',
    position: "absolute",
    right: 0,
    bottom: "800px",
    width: "200px",
    height: "90%",
    backgroundImage: "url(/images/rental-after.png)",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right bottom",
    backgroundSize: "contain",
    pointerEvents: "none",
    zIndex: 1,
  };

  return (
    <>
      <div className="New-Rnt-Dashboard" style={sectionStyle}>
        {/* <Header /> */}
        <div className="before-image" style={beforeImageStyle}></div>
        <div className="after-image" style={afterImageStyle}></div>
        <div className="Main-div">
          <TopNavBar />
          <div className="TopSearchBar">
            <TopSearchBar />
          </div>
          <div className="container">
            <div className="mb-5 mt-3">
              <RntMiddle initialFilters={filters} />
            </div>

            <div
              className="BuildingProductList"
              // style={{
              //   width: "90%",
              //   alignContent: "center",
              //   margin: "auto",
              //   display: "flex",
              // }}
            >
              {/* LEFT FILTER BOX (styled same as ProductList) */}
              <div className="col-lg-3">
                <div className="advanced-filter-card p-3 mt-5">
                  <RentalAdvancedSearch
                    filters={filters}
                    setFilters={setFilters}
                  />
                </div>
              </div>

              {/* RIGHT SIDE RESULT LIST */}
              <div className="col-lg-9">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h6 className="m-0">
                    Search results ({filteredRentals.length})
                  </h6>

                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-sm btn-outline-dark"
                      onClick={() =>
                        setFilters((prev) => {
                          const updated = { ...prev };
                          delete updated.productQuality;
                          return updated;
                        })
                      }
                    >
                      All
                    </button>

                    <button
                      className="btn btn-sm btn-outline-success"
                      onClick={() =>
                        setFilters((prev) => ({
                          ...prev,
                          productQuality: "Genuine",
                        }))
                      }
                    >
                      Genuine
                    </button>

                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() =>
                        setFilters((prev) => ({
                          ...prev,
                          productQuality: "Aftermarket",
                        }))
                      }
                    >
                      Aftermarket
                    </button>
                  </div>
                </div>

                {/* PRODUCT LIST STYLE WRAPPER */}
                <div className="product-list-container">
                  {/* HEADER ROW (matches ProductList UI but with Rental titles) */}
                  <div className="product-header-row">
                    <div className="head-cell name">Rental Item</div>
                    <div className="head-cell">Brand</div>
                    <div className="head-cell">Model</div>
                    <div className="head-cell">Category</div>
                    <div className="head-cell">Region</div>
                    <div className="head-cell">Year</div>
                    <div className="head-cell icon">View</div>
                  </div>
                  <div className="card">
                    {/* DATA ROWS */}
                    {filteredRentals?.length > 0 ? (
                      filteredRentals.map((rental, index) => (
                        <div className="product-row-card m-1" key={index}>
                          <div className="product-row">
                            <div className="cell name">
                              <Link
                                to={`/rental/product/single/${rental._id}`}
                                style={{
                                  textDecoration: "none",
                                  color: "#1a53a4",
                                }}
                              >
                                {getProductName(rental)}
                              </Link>
                            </div>

                            <div className="cell">
                              {toTitleCase(getBrandName(rental.brandId))}
                            </div>

                            <div className="cell">
                              {toTitleCase(getModelName(rental.modelId))}
                            </div>

                            <div className="cell">
                              {toTitleCase(getCategoryName(rental.categoryId, rental))}
                            </div>

                            <div className="cell">
                              {toTitleCase(getRegionName(rental.regionId))}
                            </div>

                            <div className="cell">{getProductYear(rental)}</div>


                            <div className="cell icon">
                              <Link
                                to={`/rental/product/single/${rental._id}`}
                                style={{ textDecoration: "none" }}
                              >
                                <img src="/images/go.png" alt="view" />
                              </Link>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-4">
                        <NoData />
                      </div>
                    )}
                  </div>
                </div>

                {/* PAGINATION */}
                <div className="pagination-container d-flex justify-content-end align-items-center mt-4">
                  <button
                    className="pagination-btn"
                    disabled={page === 1}
                    onClick={() => handlePageChange(page - 1)}
                  >
                    <i className="fa fa-arrow-left"></i>
                  </button>

                  <span className="mx-3">
                    {page} of {totalPages}
                  </span>

                  <button
                    className="pagination-btn"
                    disabled={page === totalPages}
                    onClick={() => handlePageChange(page + 1)}
                  >
                    <i className="fa fa-arrow-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* <Footer /> */}
      </div>
    </>
  );
}
